import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Alert } from 'reactstrap';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (confirmationCode: string) => void;
  catalogueName: string;
  loading?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  catalogueName,
  loading = false
}) => {
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    if (!confirmationCode.trim()) {
      setError('Please enter the confirmation code');
      return;
    }
    
    if (confirmationCode.toLowerCase() !== 'delete') {
      setError('Confirmation code must be "delete"');
      return;
    }
    
    setError('');
    onConfirm(confirmationCode);
  };

  const handleClose = () => {
    if (loading) return;
    setConfirmationCode('');
    setError('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={loading ? undefined : handleClose} size="md" centered>
      <ModalHeader toggle={loading ? undefined : handleClose} className="bg-danger text-white">
        ⚠️ Delete Catalogue Confirmation
      </ModalHeader>
      <ModalBody>
        <Alert color="danger" className="mb-3">
          <strong>Warning:</strong> This action cannot be undone!
        </Alert>
        
        <p className="mb-3">
          You are about to permanently delete the catalogue:
        </p>
        
        <div className="bg-light p-3 rounded mb-3">
          <strong>{catalogueName}</strong>
        </div>
        
        <p className="mb-3 text-muted">
          To confirm deletion, please type <strong>"delete"</strong> in the box below:
        </p>
        
        <Input
          type="text"
          value={confirmationCode}
          onChange={(e) => {
            setConfirmationCode(e.target.value);
            setError('');
          }}
          placeholder="Type 'delete' to confirm"
          className={error ? 'is-invalid' : ''}
          disabled={loading}
        />
        
        {error && (
          <div className="invalid-feedback d-block">
            {error}
          </div>
        )}
        
        <div className="mt-3">
          <small className="text-muted">
            <strong>Note:</strong> This will permanently delete the catalogue and all its data. 
            Make sure you have backed up any important information.
          </small>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          color="danger"
          onClick={handleConfirm}
          disabled={loading || !confirmationCode.trim()}
        >
          {loading ? 'Deleting...' : 'Delete Catalogue'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteConfirmationModal;
